import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { formFields } from "../helper/templateconstant";
import { Card, CardBody, Container, Col, Row, Button } from "reactstrap";

import CommonTabHeader from "Components/Common/CommonTabHeader";
import { showToastMessage } from "common/service/CommonService";
import { LIBRARY_PATHS } from "Routes/paths";
import MonacoHtmlEditor from "../components/MonacoEditor";
import { useTemplateApi } from "modules/Library/Template/api/useTemplateService";
import Reason, {
  ReasonSubmitPayload,
} from "../../../../common/utilities/Reason/Reason";
import { queryClient } from "api/queryClient";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import Template from "../helper/template.types";
import { BreadcrumbContext } from "Layouts/Header";

const EditTemplate: React.FC = () => {
  document.title = "Edit Template | ZENTIXS VISTA";

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const templateId = Number(id);

  const [htmlContent, setHtmlContent] = useState<string>("");
  const [showPreview, setShowPreview] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showReason, setShowReason] = useState(false);

  // Form Setup
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      templateName: "",
    },
    mode: "onChange",
  });

  const templateName = watch("templateName");
  const breadcrumbContext = useContext(BreadcrumbContext);

  // API Hooks
  const { DetailSelectQuery, UpdateMutation } = useTemplateApi({
    templateid: templateId,
    selectAllEnabled: false,
  });

  // ✅ Proper HTML validation
  const isHtmlEmpty = (html: string) => {
    return !html || html.trim() === "";
  };

  // ✅ Initial Load from storage/location state or API
  useEffect(() => {
    if (initialized) return;

    if (location.state) {
      const stateData = location.state as Template;
      const name =stateData.templatename || "";
      const html = stateData.designtemplate || "";
      setValue("templateName", name);
      setHtmlContent(html);
      setInitialized(true);
      return;
    }

    if (DetailSelectQuery.data?.data) {
      const apiData =
        DetailSelectQuery.data.data[0] || DetailSelectQuery.data.data;
      const name =  apiData.templatename || "";
      const html =  apiData.designtemplate || "";
      setValue("templateName", name);
      setHtmlContent(html);
      setInitialized(true);
    }
  }, [location.state, DetailSelectQuery.data, initialized, setValue]);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Template", link: LIBRARY_PATHS.TEMPLATE_LIST },
      { text: templateName || "Edit Template" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const handleUpdate = () => {
    if (isHtmlEmpty(htmlContent)) {
      showToastMessage("error", "Please enter the HTML template content");
      return;
    }
    setShowReason(true);
  };

  const handleReasonSubmit = (payload: ReasonSubmitPayload) => {
    UpdateMutation.mutate(
      {
        templateid: templateId,
        name: templateName,
        html: htmlContent,
        updatereason: payload.reason,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage("success", res?.message || "Template updated successfully");
          setShowReason(false);
          queryClient.invalidateQueries({ queryKey: ["template-selectAll"] });
          navigate(LIBRARY_PATHS.TEMPLATE_LIST);
        },
        onError: (error: any) => {
          showToastMessage("error", (error?.message ?? error) || "Failed to update template");
        },
      },
    );
  };

  const isLoading = DetailSelectQuery.isLoading || UpdateMutation.isPending;

  return (
    <Container
      fluid
      style={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "stretch",
      }}
    >
      <CommonTabHeader
        title="Edit Template"
        showAction={true}
        showActionInHeader={false}
        activeTab="Update Template"
        showActionHandler={handleSubmit(handleUpdate)}
        exitPath={LIBRARY_PATHS.TEMPLATE_LIST}
        isOpen={UpdateMutation.isPending}
      />

      <Row className="pt-2 flex-grow-1 overflow-hidden">
        <Col lg={12} className="h-100 pb-2">
          <Card className="h-100 shadow-none border-0 mb-0">

            <CardBody className="p-3 d-flex flex-column h-100 overflow-hidden">
              <div className="text-end template_btn_container">
                <Button
                  size="sm"
                  color="primary"
                  outline
                  onClick={handleSubmit(handleUpdate)}
             
                >
           
                  Update Template
                </Button>
              </div>
              {isLoading && !initialized ? (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* FORM FIELDS */}
                  <CommonForm
                    fields={formFields}
                    control={control}
                    errors={errors}
                    columns={1}
                    setValue={setValue}
                  />

                  {/* INTEGRATED EDITOR / PREVIEW */}
                  <div
                    className="flex-grow-1 mt-3 overflow-hidden w-100"
                    style={{ minHeight: "0" }}
                  >
                    <MonacoHtmlEditor
                      value={htmlContent}
                      onChange={setHtmlContent}
                      height="100%"
                      showPreview={showPreview}
                      onTogglePreview={() => setShowPreview(!showPreview)}
                    />
                  </div>

                  {/* NOTE - Stay at bottom */}
                  <div className="mt-2 small text-muted pt-2 border-top">
                    <strong>Note:</strong> Use placeholders like{" "}
                    <code>{"{{FullName}}"}</code>, <code>{"{{Barcode}}"}</code>,{" "}
                    <code>{"{{SubjectNo}}"}</code>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Update Reason Modal */}
      <Reason
        isOpen={showReason}
        toggle={() => setShowReason(false)}
        action="Update"
        id={templateId}
        click={handleReasonSubmit}
        isLoading={UpdateMutation.isPending}
      />
    </Container>
  );
};

export default EditTemplate;
